import { IActionFn, IPageData } from '@/store/types';
import { IModuleState } from './types';
import { $http } from '@/axios';
import store from '@/store';

// 查询组织列表
export const queryOrgPage: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setOrgPage', { ...state.orgPage, loading: true });
    const res: IPageData = await $http.post('/v1/data/system/org/pagelist', payload);
    const { list: data, pageNum, pageSize, total } = res;
    commit('setOrgPage', { data, pageNum, pageSize, total });
  } catch (e) {
    commit('setOrgPage', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询组织详情
export const queryOrgInfo: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const cache = payload.clearCache === false ? state.orgInfo.data : null;
    commit('setOrgInfo', { data: cache, loading: true });
    const res = await $http.post('/v1/data/system/org/detail', payload);
    commit('setOrgInfo', { data: res });
  } catch (e) {
    commit('setOrgInfo', { data: null });
  }
};

// 查询客户类型列表
export const queryCustomerTypes: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setCustomerTypes', { ...state.customerTypes, loading: true });
    const res = await $http.get('/common/chooseoptions/OrgAdditionalType');
    commit('setCustomerTypes', { data: res });
  } catch (e) {
    commit('setCustomerTypes', { data: [] });
  }
};

// 添加会员/企业
export const addOrg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    await $http.post('/v1/system/org/add', payload);
  } catch (e) {
    store.commit('setLoading', false);
  }
};

// 添加试用会员
export const addTrialMember: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    await $http.post('/v1/system/org/addtrial', payload);
  } catch (e) {
    store.commit('setLoading', false);
  }
};

// 编辑组织
export const editOrg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    await $http.post('/v1/system/org/modify', payload);
  } catch (e) {
    store.commit('setLoading', false);
  }
};

// 激活组织
export const activeOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/baseinfo/enterprise/activation', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 锁定组织
export const lockOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    const postUrl = !!payload.memberId ? '/v1/system/member/lock' : '/v1/system/org/lock';
    await $http.post(postUrl, payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除组织
export const deleteOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const isMember = !!payload.id;
    const postUrl = isMember ? '/v1/system/member/delete' : '/v1/system/org/delete';
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post(postUrl, payload);
    store.commit('common/setToast', { visible: true, text: '删除成功' });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 升级组织(到期后降级的会员或自注册的会员升级为收费会员)
export const upgradeOrg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    await $http.post('/v1/system/org/modifytoupgrade', payload);
    store.commit('common/setToast', { visible: true, text: '升级成功' });
  } finally {
    store.commit('setLoading', false);
  }
};

// 启用子组织
export const enableSubOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/check/org/supportsuborg', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 禁用子组织
export const disableSubOrg: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/check/org/supportsuborg', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 检查用户登录名是否重复
export const checkLoginExist: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const notExist = await $http.post('/v1/check/user/isloginvalid', payload);
    return notExist;
  } catch (e) {
    return false;
  }
};

// 检查组织下是否有活跃用户和子组织
export const checkOrgActivate: IActionFn<IModuleState> = async (_, orgId) => {
  try {
    const params = { pageNum: 1, pageSize: 1, orgId };
    const fetchUsers: Promise<IPageData> = $http.post( '/v1/baseinfo/user/pagelist', { ...params, status: [1] });
    const fetchSubOrgs: Promise<IPageData> = $http.post( '/v1/system/suborg/search', params);
    const [users, subOrgs] = await Promise.all([fetchUsers, fetchSubOrgs]);
    return (users.total > 0) || (subOrgs.total > 0);
  } catch (e) {
    return false;
  }
};

// 检查组织的总账号是否小于已使用账号数量
export const checkUserLimit: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const overcapacity = await $http.post('/v1/data/system/org/overcapacity', payload);
    return overcapacity;
  } catch (e) {
    return false;
  }
};

// 创建订单(新建组织)
export const createOrder: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOrderResult', { data: null, loading: true });
    const res = await $http.post('/v1/finance/org/serviceadjust', payload);
    commit('setOrderResult', { data: res });
  } catch (e) {
    commit('setOrderResult', { data: null });
  }
};

// 支付订单(创建并激活组织)
export const paidOrder: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    const res = await $http.post('/v1/finance/org/addpaymentandpay', payload);
    commit('setOrderOrg', { data: res });
    store.commit('common/setToast', { visible: true, text: '支付成功' });
    return true;
  } catch (e) {
    return false;
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 计算价格(创建组织所需的费用)
export const calcPrice: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOrderPrice', { data: null, loading: true });
    const res: any = await $http.post('/v1/finance/org/calcserviceprice', payload);
    commit('setOrderPrice', { data: res?.totalAmount == null ? null : res.totalAmount });
  } catch (e) {
    commit('setOrderPrice', { data: null });
  }
};

// 查询品牌企业列表
export const queryOwnerOrgs: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOwnerOrgs', { data: [], loading: true });
    const res = await $http.post('', payload);
    commit('setOwnerOrgs', { data: res });
  } catch (e) {
    commit('setOwnerOrgs', { data: [] });
  }
};

// 查询品牌企业的下辖品牌列表(待保存的门店品牌)
export const queryOwnerBrands: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setOwnerBrands', { data: [], loading: true });
    const res = await $http.post('', payload);
    commit('setOwnerBrands', { data: res });
  } catch (e) {
    commit('setOwnerBrands', { data: [] });
  }
};

// 查询品牌门店的下辖品牌列表(已保存的门店品牌)
export const queryStoreBrands: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setStoreBrands', { data: [], loading: true });
    const res = await $http.post('', payload);
    commit('setStoreBrands', { data: res });
  } catch (e) {
    commit('setStoreBrands', { data: [] });
  }
};

// 查询待添加的竞品或授权品牌列表
export const queryUnauthBrands: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setUnauthBrands', { ...state.unauthBrands, loading: true });
    const res: IPageData = await $http.post('', payload);
    const { list: data, pageNum, pageSize, total } = res;
    commit('setUnauthBrands', { data, pageNum, pageSize, total });
  } catch (e) {
    commit('setUnauthBrands', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询已保存的授权品牌列表
export const queryAuthedBrands: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setAuthedBrands', { ...state.authedBrands, loading: true });
    const res: IPageData = await $http.post('', payload);
    const { list: data, pageNum, pageSize, total } = res;
    commit('setAuthedBrands', { data, pageNum, pageSize, total });
  } catch (e) {
    commit('setAuthedBrands', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询已保存的竞品品牌列表
export const queryCompetBrands: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setCompetBrands', { ...state.competBrands, loading: true });
    const res: IPageData = await $http.post('', payload);
    const { list: data, pageNum, pageSize, total } = res;
    commit('setCompetBrands', { data, pageNum, pageSize, total });
  } catch (e) {
    commit('setCompetBrands', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 添加授权品牌
export const addAuthBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/system/org/authbrand/add', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 删除授权品牌
export const deleteAuthBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/system/org/authbrand/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 添加竞品品牌
export const addCompetBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/system/org/competitivebrand/add', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 删除竞品品牌
export const deleteCompetBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/system/org/competitivebrand/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 查询客服沟通列表
export const queryExchanges: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setExchanges', { data: [], loading: true });
    const res = await $http.post('', payload);
    commit('setExchanges', { data: res });
  } catch (e) {
    commit('setExchanges', { data: [] });
  }
};

// 添加沟通记录
export const addExchange: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', true);
    await $http.post('/v1/operation/csnotification/add', payload);
    store.commit('common/setToast', { visible: true, text: '成功添加沟通记录' });
  } finally {
    commit('setLoading', false);
  }
};
